// @ts-nocheck
import curry from 'lodash/curry';
import { SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { RUMHelpers } from 'services/observability/datadog';
import { UpdatedStyledConditionBadge } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/styles';
import { BADGE_VARIANTS } from 'ui/badge';
import type { BaseOption } from 'ui/actionMenu/types';
import {
  EVENT_OR_PROPERTY_LEADING_TEXT,
  EVENT_OR_PROPERTY_TRAILING_TEXT,
} from 'modules/predictiveGoals/constants';
import { TemplateEventPopoverContent } from 'modules/predictiveGoals/types';
import { PropertyBase } from './PropertyBase';
import { EventBase } from './EventBase';
import { createEventCondition } from '../utils/queryHelpers';
import { GOAL_TYPES, CONDITIONAL_TEXT } from '../constants';

import {
  AndQueryContainer,
  QueryBaseContainerAndQuery,
  UpdatedOrActionButtons,
  UpdatedButtonWrapper,
} from '../Styled';

export function createEventInput(orQuery) {
  if (!Object.hasOwnProperty.call(orQuery, 'type')) {
    return {};
  }

  const eventConditions = createEventCondition(orQuery.type, orQuery);

  return {
    ...orQuery,
    eventConditions,
  };
}

export function createOrQueriesClause(
  commonProps,
  properties,
  events,
  andQueryIdx,
  orQueryIdx,
  orQuery,
  templatePopoverContent,
) {
  if (orQuery.type === GOAL_TYPES.property) {
    return (
      <PropertyBase
        {...commonProps}
        queryIdx={orQueryIdx}
        properties={properties}
        query={orQuery}
      />
    );
  } else {
    const eventQuery = createEventInput(orQuery);
    return (
      <EventBase
        {...commonProps}
        andQueryIdx={andQueryIdx}
        orQueryIdx={orQueryIdx}
        events={events}
        query={eventQuery}
        templatePopoverContent={templatePopoverContent}
      />
    );
  }
}

export type AndQueryProps = {
  id: string;
  onUpdate?: (Array) => void;
  onDelete?: (number) => void;
  orQueries: Array;
  properties?: BaseOption[];
  events?: BaseOption[];
  duration?: string;
  templatePopoverContent: TemplateEventPopoverContent;
};

export const AndQuery = ({
  duration,
  events,
  id,
  onDelete,
  onUpdate,
  orQueries = [],
  properties,
  templatePopoverContent,
}: AndQueryProps) => {
  const updateQuery = (queryIdx, data) => {
    onUpdate(
      orQueries.map((query, idx) => {
        if (idx === queryIdx) {
          return { ...data };
        } else {
          return query;
        }
      }),
    );
  };

  const deleteQuery = (queryIdx) => {
    const newOrQueries = [...orQueries];
    newOrQueries.splice(queryIdx, 1);
    onUpdate(newOrQueries);
    if (newOrQueries.length === 0) {
      onDelete();
    }
  };

  const addEventQuery = () => {
    RUMHelpers.addRUMAction({ actionName: 'Add event to or query' });
    onUpdate([...orQueries, { type: undefined, window: duration }]);
  };

  const addPropertyQuery = () => {
    RUMHelpers.addRUMAction({ actionName: 'Add property to or query' });
    onUpdate([...orQueries, { type: 'user', window: duration }]);
  };

  const queryOrClauses = orQueries.map((orQuery, queryIdx) => {
    const commonProps = {
      id: queryIdx,
      key: queryIdx,
      onUpdate: curry(updateQuery)(queryIdx),
      onDelete: deleteQuery.bind(this, queryIdx),
      orQueriesAdded: orQueries,
    };

    return createOrQueriesClause(
      commonProps,
      properties,
      events,
      id,
      queryIdx,
      orQuery,
      templatePopoverContent,
    );
  });

  return (
    <AndQueryContainer data-test={`${id}_AndQuery`}>
      {queryOrClauses}
      {
        <QueryBaseContainerAndQuery>
          {
            <UpdatedOrActionButtons>
              <UpdatedStyledConditionBadge variant={BADGE_VARIANTS.READY}>
                {CONDITIONAL_TEXT.or.toUpperCase()}
              </UpdatedStyledConditionBadge>
              <UpdatedButtonWrapper>
                <SecondaryButton
                  buttonSize={BUTTON_SIZES.SMALL}
                  onClick={addEventQuery}
                  id={'add-event-button'}
                >
                  <span>{EVENT_OR_PROPERTY_LEADING_TEXT.event}</span>
                </SecondaryButton>
                <SecondaryButton
                  buttonSize={BUTTON_SIZES.SMALL}
                  onClick={addPropertyQuery}
                  id="add-property-button"
                >
                  <span>{EVENT_OR_PROPERTY_TRAILING_TEXT.property}</span>
                </SecondaryButton>
              </UpdatedButtonWrapper>
            </UpdatedOrActionButtons>
          }
        </QueryBaseContainerAndQuery>
      }
    </AndQueryContainer>
  );
};
